<template>
  <b-container fluid class="mb-4">
    <b-row>
      <Filters />
      <b-col class="search-section">
        <SearchHeader />
        <SlicesBlock v-if="existContent('topContent')" :slices="topContent(dimension)" />
        <SearchWildcard />
        <WithoutResult />
        <GridCSS :items="searchResult" :send-vector-click="true" @item-click="vectorClick">
          <template #default="slotProps">
            <NormalVectorOverlay :data="slotProps.data" />
          </template>
        </GridCSS>
        <Banner />
        <NotEnoughResults />
        <SearchPaginator class="mt-3" />
        <SlicesBlock v-if="existContent('bottomContent')" :slices="bottomContent(dimension)" />
      </b-col>
    </b-row>
  </b-container>
</template>

<script>
import { uids } from '~/customHtmls.json';
import { getOGImage } from '~/utils/common.js';

import { mapState, mapGetters } from 'vuex';

import Filters from '@/components/SearchResults/Filters';
import SearchHeader from '@/components/SearchResults/SearchHeader';
import SearchWildcard from '@/components/SearchResults/SearchWildcard';
import WithoutResult from '@/components/SearchResults/WithoutResult';
import NotEnoughResults from '@/components/SearchResults/NotEnoughResults';
import GridCSS from '@/components/Common/GridCSS';
import NormalVectorOverlay from '@/components/Vectors/NormalVectorOverlay';
import SearchPaginator from '@/components/SearchResults/SearchPaginator';
import SlicesBlock from '@/components/Prismic/CustomHtml/SlicesBlock.vue';
import Banner from '@/components/SearchResults/Banner';

export default {
  components: {
    SearchPaginator,
    SearchWildcard,
    SearchHeader,
    Filters,
    GridCSS,
    NormalVectorOverlay,
    WithoutResult,
    NotEnoughResults,
    SlicesBlock,
    Banner
  },
  props: {
    dimension: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      seoFields: { canonical: {} }
    };
  },
  async fetch() {
    const locale = this.$store.$i18n.locale;
    const search = this.$route.params.search;
    let regexPages = /((-p)$|(-p-page))/;
    let slugName = this.getRouteBaseName().replace(regexPages, '');

    const slugs = {
      en: this.localePath({ name: slugName, params: { slug: search } }, 'en'),
      de: this.localePath({ name: slugName, params: { slug: search } }, 'de'),
      es: this.localePath({ name: slugName, params: { slug: search } }, 'es'),
      pt: this.localePath({ name: slugName, params: { slug: search } }, 'pt')
    };

    this.seoFields.canonical = this.$canonicalPlugin(slugs[`${locale}`], locale, true);

    const currentPage = this.$route.params.page ? parseInt(this.$route.params.page) : 1;
    const currentSearch = search ? search.replace(/-/g, ' ') : '';
    let promises = [];
    if (this.getSilentSearch) {
      this.$store.commit('search/SET_SILENT_SEARCH', false);
    } else {
      const sidebarPromise = this.$store.dispatch('recommendations/LOAD_SIDE_BAR_RECOMMENDATIONS');
      const searchPromise = this.$store.dispatch('search/MAKE_SEARCH', {
        criteria: currentSearch,
        dimension: this.dimension,
        page: currentPage
      });
      promises = promises.concat([searchPromise, sidebarPromise]);
    }

    const dimensionUid = `dimension-${this.dimension}`;
    const hasCustomHtml = search
      ? uids.searches.find((el) => el[locale] === search)
      : uids.dimensions.find((el) => el[locale] === dimensionUid);

    if (hasCustomHtml) {
      const customHtmlPromise = this.$store.dispatch('customHtml/LOAD_CUSTOM_HTML', {
        uid: search || dimensionUid,
        dimension: this.dimension
      });
      promises.push(customHtmlPromise);
    } else {
      this.$store.commit('customHtml/SET_ACTUAL_CONTENT', {});
      this.$store.commit('customHtml/SET_CUSTOM_HTML', {});
    }

    return Promise.all(promises);
  },
  head() {
    const i18nSeo = this.$seoPlugin({ hreflang: false, canonical: false, slash: true });
    const openGraph = this.$openGraphPlugin(
      this.metadata?.metaTitle,
      'website',
      this.$route.path,
      this.metadata?.metaDescription,
      this.getOpenGraphImage,
      this.$store.state.i18n.locale
    );
    return {
      title: this.metadata?.metaTitle,
      meta: [
        {
          hid: 'description',
          name: 'description',
          content: this.metadata?.metaDescription
        },
        {
          hid: 'robots',
          name: 'robots',
          content: this.hasLittleContent || this.isPaged ? 'noindex, follow' : 'all'
        },
        ...openGraph
      ],
      link: [this.seoFields.canonical, ...i18nSeo.link]
    };
  },
  computed: {
    ...mapState('search', ['searchResult', 'wildcard', 'metadata', 'currentPage', 'totalPages']),
    ...mapGetters('search', ['getImageFirstResult', 'getSilentSearch']),
    ...mapState('searchBar', ['actualSearch']),
    ...mapGetters('dimensions', ['getDimensionsByExperience', 'getActualDimension']),
    ...mapGetters('customHtml', ['existContent', 'topContent', 'bottomContent']),
    hasLittleContent() {
      return this.searchResult.length == 0;
    },
    isPaged() {
      return this.currentPage > 1;
    },
    getOpenGraphImage() {
      return this.$route.params.search ? this.getImageFirstResult : getOGImage(this.$expKey());
    }
  },
  beforeDestroy() {
    if (!this.getSilentSearch) {
      this.$store.dispatch('search/CLEAN_SEARCH');
      this.$store.dispatch('dimensions/ACTUAL_DIMENSION', 'All');
    }
  },
  methods: {
    async vectorClick({ item, shownListing }, e) {
      e.preventDefault();
      this.$bvModal.show('preview-vector-modal');
      await this.$store.dispatch('vectors/OPEN_PREVIEW', { id: item.id, list: shownListing });
    }
  }
};
</script>
